import { useMemo } from "react";
import { useEntityFieldsWithAdditionals } from "@/pages/settings/hooks";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { GlobalSearchEntityTypes } from "../types";
import { camelCase, startCase } from "lodash";

export const useSearchFieldLabels = () => {
  const { data: companiesFields } = useEntityFieldsWithAdditionals(
    EntityType.COMPANY
  );
  const { data: dealsFields } = useEntityFieldsWithAdditionals(EntityType.DEAL);
  const { data: leadsFields } = useEntityFieldsWithAdditionals(EntityType.LEAD);
  const { data: peopleFields } = useEntityFieldsWithAdditionals(
    EntityType.PERSON
  );
  const { data: contractsFields } = useEntityFieldsWithAdditionals(
    EntityType.CONTRACT
  );

  const fieldLabelMap = useMemo(() => {
    const map: Record<string, Record<string, string>> = {
      [GlobalSearchEntityTypes.COMPANIES]: {},
      [GlobalSearchEntityTypes.DEALS]: {},
      [GlobalSearchEntityTypes.LEADS]: {},
      [GlobalSearchEntityTypes.PEOPLE]: {},
      [GlobalSearchEntityTypes.CONTRACTS]: {}
    };

    companiesFields?.fields?.forEach((field) => {
      map[GlobalSearchEntityTypes.COMPANIES][field.fieldKey.toLowerCase()] =
        field.label;
    });

    dealsFields?.fields?.forEach((field) => {
      map[GlobalSearchEntityTypes.DEALS][field.fieldKey.toLowerCase()] =
        field.label;
    });

    leadsFields?.fields?.forEach((field) => {
      map[GlobalSearchEntityTypes.LEADS][field.fieldKey.toLowerCase()] =
        field.label;
    });

    peopleFields?.fields?.forEach((field) => {
      map[GlobalSearchEntityTypes.PEOPLE][field.fieldKey.toLowerCase()] =
        field.label;
    });

    contractsFields?.fields?.forEach((field) => {
      map[GlobalSearchEntityTypes.CONTRACTS][field.fieldKey.toLowerCase()] =
        field.label;
    });

    return map;
  }, [
    companiesFields,
    dealsFields,
    leadsFields,
    peopleFields,
    contractsFields
  ]);

  const getFieldLabel = (
    entityType: GlobalSearchEntityTypes,
    fieldKey: string
  ): string => {
    let keyToConvert = fieldKey;
    let suffix = "";

    if (fieldKey.endsWith("_c")) {
      keyToConvert = fieldKey.slice(0, -2);
      suffix = "_c";
    }

    const camelCaseKey = camelCase(keyToConvert) + suffix;
    const label = fieldLabelMap[entityType]?.[camelCaseKey.toLowerCase()];

    if (label) {
      return label;
    }

    const formatted = startCase(keyToConvert).toLowerCase();
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  return { getFieldLabel };
};
